import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./buscadorFiltro.css";
import Input from "../../Elements/Input/Input";
import Fuse from "fuse.js";

export default function BuscadorForms({
  search,
  setSearch,
  data = [],
  onResults,
  keys = [
    { name: "cliente", weight: 0.55 },
    { name: "noOT", weight: 0.25 },
    { name: "submittedBy.username", weight: 0.15 },
    { name: "submittedBy.role", weight: 0.05 },
  ],
  maxSuggestions = 6,
  minChars = 2,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // ✅ para evitar loop (recordar último resultado enviado)
  const lastResultSigRef = useRef("");

  const stripAccents = useCallback(
    (str = "") =>
      String(str)
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase(),
    []
  );

  // ✅ string "a.b" o array ["a","b"]
  const getByPath = useCallback((obj, path) => {
    if (!obj || path == null) return "";

    const parts = Array.isArray(path)
      ? path
      : typeof path === "string"
      ? path.split(".")
      : [String(path)];

    return parts.reduce((acc, key) => (acc != null ? acc[key] : undefined), obj) ?? "";
  }, []);

  const tokenRegex = useMemo(() => /"([^"]+)"|(\S+)/g, []);
  const parseTokens = useCallback(
    (q) => {
      const tokens = [];
      if (!q) return tokens;
      let m;
      tokenRegex.lastIndex = 0;
      while ((m = tokenRegex.exec(q))) {
        const raw = m[1] || m[2];
        const [k, ...rest] = raw.split(":");
        if (rest.length) {
          tokens.push({ type: "kv", key: k.toLowerCase(), value: stripAccents(rest.join(":")) });
        } else {
          tokens.push({ type: "word", value: stripAccents(raw) });
        }
      }
      return tokens;
    },
    [stripAccents, tokenRegex]
  );

  const fuseKeys = useMemo(
    () => keys.map((k) => (typeof k === "string" ? { name: k, weight: 1 } : k)),
    [keys]
  );

  const fuse = useMemo(() => {
    if (!data?.length) return null;
    return new Fuse(data, {
      includeScore: true,
      threshold: 0.35,
      ignoreLocation: true,
      keys: fuseKeys,
      getFn: (obj, path) => stripAccents(String(getByPath(obj, path) ?? "")),
    });
  }, [data, fuseKeys, stripAccents, getByPath]);

  const filterDeterministic = useCallback(
    (q, base = data) => {
      if (!base?.length) return [];
      const tokens = parseTokens(q);
      if (!tokens.length) return base;

      const fieldMap = {
        cliente: "cliente",
        ot: "noOT",
        noot: "noOT",
        firmado: "submittedBy.username",
        firmadopor: "submittedBy.username",
        usuario: "submittedBy.username",
        user: "submittedBy.username",
        role: "submittedBy.role",
        puesto: "submittedBy.role",
      };

      const fields = fuseKeys.map((k) => (Array.isArray(k.name) ? k.name.join(".") : k.name));

      return base.filter((row) => {
        const bag = fields
          .map((f) => stripAccents(String(getByPath(row, f) ?? "")))
          .join(" ");

        return tokens.every((t) => {
          if (t.type === "word") return bag.includes(t.value);

          if (t.type === "kv") {
            const mapped = fieldMap[t.key];
            if (!mapped) return true;
            const v = stripAccents(String(getByPath(row, mapped) ?? ""));
            return v.includes(t.value);
          }

          return true;
        });
      });
    },
    [data, fuseKeys, parseTokens, stripAccents, getByPath]
  );

  const buildSuggestions = useCallback(
    (q) => {
      if (!fuse || !q || stripAccents(q).length < minChars) return [];

      const localFuse = new Fuse(data, {
        includeScore: true,
        threshold: 0.4,
        ignoreLocation: true,
        keys: ["cliente", "noOT", "submittedBy.username"],
        getFn: (obj, path) => stripAccents(String(getByPath(obj, path) ?? "")),
      });

      const tokens = parseTokens(q).filter((t) => t.type === "word");
      const term = tokens.length ? tokens[0].value : stripAccents(q);

      return localFuse
        .search(term)
        .slice(0, maxSuggestions)
        .map((r) => {
          const cliente = r.item?.cliente ?? "";
          const ot = r.item?.noOT ?? "";
          const firmado = r.item?.submittedBy?.username ?? "";
          return {
            label: `${cliente || "Sin cliente"}${ot ? ` · OT ${ot}` : ""}${firmado ? ` · ${firmado}` : ""}`.trim(),
            value: `${cliente} ${ot} ${firmado}`.trim(),
          };
        });
    },
    [data, fuse, maxSuggestions, minChars, parseTokens, stripAccents, getByPath]
  );

  // ✅ firma del resultado para evitar loops (IDs)
  const buildResultSignature = useCallback((arr) => {
    const ids = (Array.isArray(arr) ? arr : [])
      .map((x) => x?.FormId)
      .filter(Boolean)
      .sort();
    return `${ids.length}|${ids.join(",")}`;
  }, []);

  useEffect(() => {
    // si no hay data, manda [] solo si cambió
    if (!data?.length) {
      const sig = "0|";
      if (lastResultSigRef.current !== sig) {
        lastResultSigRef.current = sig;
        onResults?.([]);
      }
      return;
    }

    const deterministic = filterDeterministic(search, data);

    // ✅ evita bucle: solo llama onResults si cambió el resultado
    const sig = buildResultSignature(deterministic);
    if (lastResultSigRef.current !== sig) {
      lastResultSigRef.current = sig;
      onResults?.(deterministic);
    }

    const shouldSuggest =
      stripAccents(search).length >= minChars && deterministic.length === 0;

    if (shouldSuggest) {
      setSuggestions(buildSuggestions(search));
      setShowSuggestions(true);
    } else {
      // evita re-render inútil
      if (suggestions.length) setSuggestions([]);
      if (showSuggestions) setShowSuggestions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    search,
    minChars,
    onResults,
    filterDeterministic,
    stripAccents,
    buildSuggestions,
    buildResultSignature,
  ]);

  useEffect(() => {
    const handler = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelectSuggestion = (s) => {
    setSearch(s.value);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || !suggestions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) handleSelectSuggestion(suggestions[activeIndex]);
      else setShowSuggestions(false);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const clearSearch = () => {
    setSearch("");
    setActiveIndex(-1);
    setSuggestions([]);
    inputRef.current?.focus();
  };

  return (
    <div className="buscador-filtros" ref={wrapperRef}>
      <div className="buscador-input" style={{ position: "relative", width: "100%" }}>
        <Input
          ref={inputRef}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => suggestions.length && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          placeholder='Busca por cliente, OT o firmado (ej: firmado:juan)'
          classNames="buscador-input-box"
        />

        {search?.length > 0 && (
          <button
            type="button"
            aria-label="Limpiar búsqueda"
            className="btn-clear"
            onClick={clearSearch}
            style={{
              position: "absolute",
              right: "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              background: "transparent",
              border: 0,
              cursor: "pointer",
              fontSize: 16,
              opacity: 0.7,
            }}
          >
            ×
          </button>
        )}

        {showSuggestions && suggestions.length > 0 && (
          <div
            className="sugerencias-dropdown"
            role="listbox"
            style={{
              position: "absolute",
              zIndex: 10,
              top: "100%",
              left: 0,
              right: 0,
              background: "white",
              border: "1px solid #E5E7EB",
              borderRadius: 10,
              marginTop: 6,
              boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "8px 12px", fontSize: 12, color: "#6B7280" }}>
              Quizá quisiste escribir...
            </div>

            {suggestions.slice(0, maxSuggestions).map((s, i) => (
              <button
                key={`${s.value}-${i}`}
                role="option"
                aria-selected={activeIndex === i}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(-1)}
                onClick={() => handleSelectSuggestion(s)}
                className={`sugerencia-item ${activeIndex === i ? "is-active" : ""}`}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 12px",
                  border: 0,
                  background: activeIndex === i ? "#F3F4F6" : "white",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}